import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { CircleNotch, CheckCircle, WarningCircle, ArrowRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { toast } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatQty = (v) => (v == null ? "—" : Number(v).toLocaleString("en-IN", { maximumFractionDigits: 3 }));

// Sep 25 2026 fix (user's explicit ask, "OK on the speed trick") - the
// warehouse-move step now auto-fires the instant Goods Receipt succeeds
// instead of waiting on a manual "Confirm & Receive" click, which never
// let the user review/change anything anyway - removes a full round
// trip from every happy-path receipt.
//
// mode="receive": opens at phase="preview" (0%), fires the Goods
// Receipt step immediately, then (on success) the warehouse-move step
// immediately after - reaching "done" (100%) with zero manual clicks
// in the happy path. Either step failing shows its own phase
// ("receipt_failed" / "relocation_failed") with its own Retry button.
//
// mode="view": jumps straight to whatever terminal phase the stored
// order data already represents (done / receipt_failed /
// relocation_failed) - never fires anything automatically.
export const ReceiptDetailModal = ({ order, mode, onClose, onSettled, isAdmin }) => {
  const [phase, setPhase] = useState(() => initialPhase(order, mode));
  const [job, setJob] = useState(null);
  const [relocation, setRelocation] = useState(order?.receipt_relocation || null);
  const [error, setError] = useState(mode === "view" ? (order?.receipt_error || order?.receipt_relocation?.error) : null);
  const [retrying, setRetrying] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    if (mode === "receive") {
      fireGoodsReceipt();
    }
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pollJob = (jobId, onDone) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/inbound-receipts/receive-status/${jobId}`);
        setJob(data);
        if (data.status === "done" || data.status === "failed") {
          clearInterval(pollRef.current);
          onDone(data);
        }
      } catch {
        // transient - next tick retries
      }
    }, 1500);
  };

  const fireGoodsReceipt = async () => {
    setPhase("preview");
    setError(null);
    try {
      const { data } = await axios.post(`${API}/inbound-receipts/${order.sto_id}/post-goods-receipt`);
      if (data.already_posted) {
        fireRelocate();
        return;
      }
      setPhase("posting_receipt");
      pollJob(data.job_id, (finished) => {
        if (finished.status === "done") {
          fireRelocate();
        } else {
          setError(finished.error || "SAP rejected the Goods Receipt.");
          setPhase("receipt_failed");
        }
      });
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not start the Goods Receipt.");
      setPhase("receipt_failed");
    }
  };

  const fireRelocate = async () => {
    setPhase("moving_stock");
    setError(null);
    try {
      const { data } = await axios.post(`${API}/inbound-receipts/${order.sto_id}/confirm-relocate`);
      pollJob(data.job_id, (finished) => {
        if (finished.status === "done" && finished.result?.status === "received") {
          setRelocation(finished.result.receipt_relocation);
          setPhase("done");
          onSettled?.();
        } else {
          setError(finished.error || finished.result?.error || "SAP rejected the warehouse move.");
          setRelocation(finished.result?.receipt_relocation || null);
          setPhase("relocation_failed");
          onSettled?.();
        }
      });
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not start the warehouse move.");
      setPhase("relocation_failed");
    }
  };

  const retryPgr = async () => {
    setRetrying(true);
    try {
      await fireGoodsReceipt();
    } finally {
      setRetrying(false);
    }
  };

  const retryRelocation = async () => {
    setRetrying(true);
    try {
      await fireRelocate();
    } finally {
      setRetrying(false);
    }
  };

  // Feb 2026, real live incident STO-001281 - SAP's own Transfer
  // Confirmation already shows this exact move succeeded, but this
  // app still shows it failed (the response was lost BEFORE the
  // lost-response fix existed, so a plain Retry would just fail again
  // with a genuine "not enough stock" - the real stock is already
  // gone, already moved). Admin-only: checks SAP directly instead of
  // re-attempting the move, and only corrects this order if every
  // line finds an unambiguous match.
  const verifyAgainstSap = async () => {
    setVerifying(true);
    try {
      const { data } = await axios.post(`${API}/admin/inbound-receipts/${order.sto_id}/verify-stuck-relocation`);
      if (data.status === "repaired") {
        toast.success(`Confirmed in SAP — ${order.sto_id} already moved successfully, marking as received.`);
        setRelocation({ status: "done", lines: data.matched?.map((m) => ({ product_id: m.product_id, ok: true, gac_id: m.gac_id })) || [] });
        setError(null);
        setPhase("done");
        onSettled?.();
      } else {
        const reasons = data.unresolved || [];
        const allGenuinelyNotFound = reasons.length > 0 && reasons.every((r) => r.reason === "no matching SAP movement found");
        const detail = reasons.map((r) => (r.product_id ? `${r.product_id}: ${r.reason}` : r.reason)).join(" | ") || "no detail from SAP.";
        toast.error(allGenuinelyNotFound
          ? `SAP has no matching movement — the warehouse move genuinely failed, Retry Warehouse Move is safe. (${detail})`
          : `Could not confirm against SAP — do not Retry yet, this may be an unrelated/ambiguous match or a temporary SAP lookup issue. (${detail})`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not verify this order against SAP.");
    } finally {
      setVerifying(false);
    }
  };

  const progressPct = { preview: 0, posting_receipt: 35, receipt_failed: 35, moving_stock: 75, relocation_failed: 75, done: 100 }[phase] ?? 0;
  // Sep 25 2026, user's ask ("more granular level update on status,
  // engages user better") - a short step label next to the bar so the
  // user always knows exactly which of the 2 real SAP steps is running.
  const STEP_LABELS = {
    preview: "Preparing…", posting_receipt: "Step 1 of 2 — Posting Goods Receipt in SAP",
    receipt_failed: "Step 1 of 2 — Goods Receipt failed",
    moving_stock: "Step 2 of 2 — Moving stock to warehouse",
    relocation_failed: "Step 2 of 2 — Warehouse move failed",
    done: "Complete",
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg" data-testid="receipt-detail-modal">
        <DialogHeader>
          <DialogTitle data-testid="receipt-detail-modal-title">{order.sto_id} — {order.ship_from_site_id} <ArrowRight size={14} className="inline mb-0.5" /> {order.ship_to_site_id}</DialogTitle>
          <DialogDescription>SAP #{order.sap_order_id}{order.ship_to_location_name ? ` · ${order.ship_to_location_name}` : ""}</DialogDescription>
        </DialogHeader>

        <div>
          <Progress value={progressPct} className="h-1.5" data-testid="receipt-detail-modal-progress" />
          <p className="text-xs text-[#667085] mt-1" data-testid="receipt-detail-modal-step-label">{STEP_LABELS[phase]}</p>
        </div>

        {(phase === "preview" || phase === "posting_receipt") && (
          <div className="space-y-3">
            <table className="w-full text-xs" data-testid="receipt-detail-modal-preview-table">
              <thead>
                <tr className="text-[#667085] text-xs">
                  <th className="text-left py-1">Product</th>
                  <th className="text-left py-1">Description</th>
                  <th className="text-right py-1">Qty</th>
                </tr>
              </thead>
              <tbody>
                {(order.items || []).map((it) => (
                  <tr key={it.line_no}>
                    <td className="py-1 font-mono text-xs">{it.product_id}</td>
                    <td className="py-1">{it.description}</td>
                    <td className="py-1 text-right">{formatQty(it.requested_qty)} {it.unit_of_measure}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {phase === "posting_receipt" && (
              <div className="flex items-center gap-2 text-sm text-[#0B6B74]" data-testid="receipt-detail-modal-posting-indicator">
                <CircleNotch size={16} className="animate-spin" /> Posting Goods Receipt in SAP…
              </div>
            )}
          </div>
        )}

        {phase === "moving_stock" && (
          <div className="flex items-center gap-2 text-sm text-[#0B6B74]" data-testid="receipt-detail-modal-moving-indicator">
            <CircleNotch size={16} className="animate-spin" /> Moving stock to {order.ship_to_location_name || "the destination warehouse"}…
          </div>
        )}

        {phase === "receipt_failed" && (
          <div className="space-y-3">
            <div className="flex items-start gap-2 bg-[#FEE4E2] border border-[#FDA29B] rounded-lg px-3 py-2.5 text-sm text-[#B42318]" data-testid="receipt-detail-modal-receipt-error">
              <WarningCircle size={16} className="shrink-0 mt-0.5" /> {error}
            </div>
            <Button size="sm" variant="outline" onClick={retryPgr} disabled={retrying} data-testid="receipt-detail-modal-retry-pgr-btn">
              {retrying ? <CircleNotch size={14} className="animate-spin mr-1" /> : null}Retry
            </Button>
          </div>
        )}

        {phase === "relocation_failed" && (
          <div className="space-y-3">
            <div className="flex items-start gap-2 bg-[#FEE4E2] border border-[#FDA29B] rounded-lg px-3 py-2.5 text-sm text-[#B42318]" data-testid="receipt-detail-modal-relocation-error">
              <WarningCircle size={16} className="shrink-0 mt-0.5" /> {error}
            </div>
            {relocation?.lines?.length > 0 && <LineResultTable lines={relocation.lines} />}
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={retryRelocation} disabled={retrying || verifying} data-testid="receipt-detail-modal-retry-relocation-btn">
                {retrying ? <CircleNotch size={14} className="animate-spin mr-1" /> : null}Retry Warehouse Move
              </Button>
              {isAdmin && (
                <Button size="sm" variant="ghost" onClick={verifyAgainstSap} disabled={retrying || verifying} data-testid="receipt-detail-modal-verify-sap-btn">
                  {verifying ? <CircleNotch size={14} className="animate-spin mr-1" /> : null}Verify against SAP
                </Button>
              )}
            </div>
          </div>
        )}

        {phase === "done" && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 bg-[#ECFDF3] border border-[#ABEFC6] rounded-lg px-3 py-2.5 text-sm text-[#027A48] font-medium" data-testid="receipt-detail-modal-done-banner">
              <CheckCircle size={16} weight="fill" />
              {relocation?.status === "skipped_same_warehouse"
                ? `Received directly into ${order.ship_to_location_name || relocation?.to || "the destination warehouse"} — no additional warehouse move was needed.`
                : <>Moved to {relocation?.to || order.ship_to_location_name}{relocation?.gac_id ? ` · GM ${relocation.gac_id}` : ""}</>}
            </div>
            {order.outbound_delivery_display_ids?.length > 0 && (
              <p className="text-xs text-[#667085]" data-testid="receipt-detail-modal-delivery-id">
                Delivery: {order.outbound_delivery_display_ids.join(", ")}
              </p>
            )}
            {relocation?.lines?.length > 0 ? <LineResultTable lines={relocation.lines} /> : <ReceivedItemsTable items={order.items} />}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const LineResultTable = ({ lines }) => (
  <table className="w-full text-xs" data-testid="receipt-detail-modal-line-results">
    <thead>
      <tr className="text-[#667085] text-xs">
        <th className="text-left py-1">Product</th>
        <th className="text-right py-1">Result</th>
      </tr>
    </thead>
    <tbody>
      {lines.map((l) => (
        <tr key={l.product_id} className="border-t border-[#EAECF0]">
          <td className="py-1.5 font-mono text-xs text-[#344054]">{l.product_id}</td>
          <td className="py-1.5 text-right font-mono text-xs">
            {l.ok ? <span className="text-[#027A48]">GM {l.gac_id}</span> : <span className="text-[#B42318]">{l.error}</span>}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

// Sep 25 2026 - fallback for the "done" phase when there's no separate
// movement to report (e.g. relocation.status="skipped_same_warehouse",
// stock landed straight into the right warehouse via Goods Receipt
// itself) - user's explicit ask: always show what actually arrived.
const ReceivedItemsTable = ({ items }) => (
  <table className="w-full text-xs" data-testid="receipt-detail-modal-received-items">
    <thead>
      <tr className="text-[#667085] text-xs">
        <th className="text-left py-1">Product</th>
        <th className="text-left py-1">Description</th>
        <th className="text-right py-1">Qty</th>
      </tr>
    </thead>
    <tbody>
      {(items || []).map((it) => (
        <tr key={it.line_no} className="border-t border-[#EAECF0]">
          <td className="py-1.5 font-mono text-xs text-[#344054]">{it.product_id}</td>
          <td className="py-1.5">{it.description}</td>
          <td className="py-1.5 text-right">{formatQty(it.requested_qty)} {it.unit_of_measure}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

function initialPhase(order, mode) {
  if (mode === "view") {
    // Nov 2026 defensive fix (real live incident, STO-001334) - never
    // trust receipt_status alone for the green "done" banner if the
    // last stored warehouse-move result was itself a failure (e.g. a
    // reconciliation bug marked the order "received" without clearing
    // a genuinely-failed receipt_relocation) - show the real failure
    // instead of a false "Complete"/"Moved to ..." next to red errors.
    if (order?.receipt_relocation?.status === "failed") return "relocation_failed";
    if (order?.receipt_status === "received") return "done";
    if (order?.receipt_error_stage === "pgr") return "receipt_failed";
    return "relocation_failed";
  }
  return "preview";
}
